import React, { FC, useEffect, useMemo, useState } from 'react';
import { useScanToPayHandlers } from '../../hooks/use-scan-to-pay-handlers';
import { OTPDrawerContent } from '../otp/otp-drawer-content';
import { IGiftCardCommonData } from '../../models/common-models';
import { useOTP } from '../../hooks/use-otp';
import { useOTPDrawerTextContent } from '../../hooks/use-otp-drawer-content';
import { LoadingSpinner } from '../loading-spinner';
import { TransactableGiftCardList } from '../list/transactable-gift-card-list';
import { useListContext, LIST_ACTIONS } from '../../context/transactable-gift-cards-context';
import { GiftCardBarcodeList } from '../list/gift-card-barcode-list';
import { OTPErrorSource } from '../../utils/consts';

interface ScanToPayDrawerContainerProps {
  // Control prop to open the drawer
  isOpen: boolean;
  onClose: () => void;
  // OTP verification state
  onOTPVerified: (verified: boolean) => void;
  shouldShowOTPContent: boolean;
  // Account activity loading state
  isAccountActivityLoading: boolean;
  singleCard?: IGiftCardCommonData | null;
}

export const ScanToPayDrawerContainerBody: FC<ScanToPayDrawerContainerProps> = ({
  onClose,
  onOTPVerified,
  isAccountActivityLoading,
  shouldShowOTPContent,
  singleCard,
  isOpen
}) => {
  const isSingleCard = Boolean(singleCard);
  // Instantiate OTP hook locally to manage lifecycle within the drawer
  const otpHook = useOTP();

  // Add loading state for barcode verification
  const [isBarcodeLoading, setIsBarcodeLoading] = useState(false);

  const { state, dispatch } = useListContext();
  const { selectedGiftCardPaymentIDs = [], isViewBarcode } = state;

  const otpDrawerContent = useOTPDrawerTextContent({
    displayPhoneNumber: otpHook.displayPhoneNumber,
    lastUsedOtpChannel: otpHook.lastUsedOtpChannel,
    hasPhoneFromResponse: otpHook.hasPhoneFromResponse,
    otpOptions: otpHook.otpOptions?.otpOptions
  });

  const { handleInlineButtonClick, handleSeparateButtonClick, handleCloseDrawer } = useScanToPayHandlers({
    onClose,
    otpHook,
    otpDrawerContent
  });

  const isContentReady = useMemo(() => {
    const hasErrorMessage = !!otpHook.otpErrorMessage;
    const shouldWaitForAccountActivity = isSingleCard && isAccountActivityLoading;
    return !shouldWaitForAccountActivity || hasErrorMessage;
  }, [isAccountActivityLoading, otpHook.otpErrorMessage]);
  const isOTPVerifyDisabled = useMemo(
    () => otpHook.verificationCode.length !== 6 || otpHook.isOTPDisabled,
    [otpHook.verificationCode.length, otpHook.isOTPDisabled]
  );

  const isOTPContentReady = useMemo(() => {
    if (!isContentReady) return false;
    if (!shouldShowOTPContent) return false;

    const hasErrorMessage = !!otpHook.otpErrorMessage;
    if (hasErrorMessage) return true;

    const hasOTPOptions = !!otpHook.otpOptions;
    const hasAuthID = !!otpHook.authID;
    const hasLastUsedChannel = !!otpHook.lastUsedOtpChannel;
    const isTextContentReady = otpDrawerContent.isOTPOptionsReadyToDisplay;

    return hasOTPOptions && (hasAuthID || hasLastUsedChannel) && isTextContentReady;
  }, [
    isContentReady,
    shouldShowOTPContent,
    otpHook.otpErrorMessage,
    otpHook.otpOptions,
    otpHook.authID,
    otpHook.lastUsedOtpChannel,
    otpDrawerContent.isOTPOptionsReadyToDisplay
  ]);

  useEffect(() => {
    if (singleCard && selectedGiftCardPaymentIDs.length === 0) {
      dispatch({ type: LIST_ACTIONS.VIEW_BARCODE, payload: true });
      dispatch({ type: LIST_ACTIONS.SET_LIST, payload: [singleCard] });
      dispatch({ type: LIST_ACTIONS.SELECT_CARD, payload: [singleCard.id] });
    }
  }, [singleCard]);

  useEffect(() => {
    if (singleCard && shouldShowOTPContent && isOpen) {
      otpHook.startOTPFlow();
    }
  }, [shouldShowOTPContent]);

  const handleClose = () => {
    onOTPVerified(false);
    if (otpHook) {
      try {
        otpHook.clearVerificationCode();
        otpHook.setValidationError('');
        otpHook.clearOtpError();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }

    onClose();
  };

  const handleBackToSelection = () => {
    dispatch({ type: LIST_ACTIONS.VIEW_BARCODE, payload: false });
    onOTPVerified(false);
  };

  const handleConfirm = () => {
    dispatch({ type: LIST_ACTIONS.VIEW_BARCODE, payload: true });

    if (shouldShowOTPContent) {
      otpHook.startOTPFlow();
    }
  };

  if (!isContentReady) {
    return <LoadingSpinner />;
  }

  if (isViewBarcode) {
    if (shouldShowOTPContent) {
      if (!isOTPContentReady) {
        return <LoadingSpinner />;
      }

      const otpProps = {
        drawerTexts: otpDrawerContent.drawerTexts,
        contactMethodToUse: otpDrawerContent.contactMethodToUse,
        verificationCode: otpHook.verificationCode,
        validationError: otpHook.validationError,
        buttonsConfig: otpDrawerContent.buttonsConfig,
        otpErrorMessage: otpHook.otpErrorMessage,
        otpErrorSource: otpHook.otpErrorSource,
        onVerificationCodeChange: otpHook.handleVerificationCodeChange,
        onInlineButtonClick: handleInlineButtonClick,
        onSeparateButtonClick: handleSeparateButtonClick,
        onCloseClick: handleCloseDrawer,
        isVerifyDisabled: isOTPVerifyDisabled,
        isLoading: isBarcodeLoading,
        shouldShowOTPContent,
        selectedGiftCardPaymentIDs,
        otpHook: {
          userEmail: otpHook.userEmail,
          authID: otpHook.authID,
          setValidationError: otpHook.setValidationError,
          setOTPDisabled: otpHook.setOTPDisabled,
          setOtpError: (message: string, source: OTPErrorSource) => {
            otpHook.setOtpErrorMessage(message);
            otpHook.setOtpErrorSource(source);
          }
        },
        onOTPVerified,
        onBarcodeDataReceived: (barcodeData: Partial<IGiftCardCommonData>[]) => {
          dispatch({ type: 'UPDATE_CARD_BARCODE', payload: barcodeData });
        },
        setIsBarcodeLoading
      };

      return <OTPDrawerContent {...otpProps} />;
    }

    const barcodesProps: {
      onClose: () => void;
      onBack?: () => void;
      onOTPVerified?: (verified: boolean) => void;
      otpVerificationInput?: {
        email: string;
        encryptedPayload: string;
        otp: string;
      };
    } = {
      onClose: handleClose,
      onOTPVerified
    };

    if (shouldShowOTPContent && otpHook.userEmail && otpHook.authID) {
      barcodesProps.otpVerificationInput = {
        email: otpHook.userEmail,
        encryptedPayload: otpHook.authID,
        otp: otpHook.verificationCode
      };
    }

    if (!isSingleCard) {
      barcodesProps.onBack = handleBackToSelection;
    }
    return <GiftCardBarcodeList {...barcodesProps} />;
  }

  return <TransactableGiftCardList onClose={onClose} onConfirm={handleConfirm} />;
};

export default ScanToPayDrawerContainerBody;
